import React, { useState } from 'react';
import { Box, Button, IconButton, Link, Stack, Typography, useTheme } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../../api/snackbarSlice';
import {
  amlMetricBoxSx,
  amlReRunButtonSx,
  amlResultContainerSx,
  amlResultFooterSx,
  getAmlMatchScoreValueSx,
  getAmlMetricLabelSx,
  getAmlResultAlertSx,
  getAmlResultSubtitleSx,
  getAmlResultTitleSx,
} from './amlScreeningStyles';
import AmlScreeningApiResultsModal from './AmlScreeningApiResultsModal';
import AmlFlaggedProfileCard from './AmlFlaggedProfileCard';
import { formatMatchScore } from './amlScreeningHelpers';

const formatCaseId = (caseId) => {
  if (caseId == null || caseId === '') return '—';
  return String(caseId);
};

const getMoreResultsLabel = (count, labels) => {
  if (count === 1) {
    return labels.moreResultsSingular ?? '+1 more result';
  }

  const template = labels.moreResultsPlural ?? '+{{count}} more results';
  return template.replace('{{count}}', String(count));
};

export default function AmlScreeningResultsPanel({ data, labels = {}, onReScreen }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [resultsModalOpen, setResultsModalOpen] = useState(false);

  const isMatched = data?.isMatched === 'Yes';
  const screeningApiResults = Array.isArray(data?.screeningApiResults) ? data.screeningApiResults : [];
  const extraResultCount = screeningApiResults.length > 1 ? screeningApiResults.length - 1 : 0;
  const match = data?.highestScoringResult ?? screeningApiResults[0] ?? null;
  const caseId = formatCaseId(data?.caseId);

  const title = labels.title ?? 'AML Screening';
  const subtitle = labels.subtitle ?? 'Anti-Money Laundering checks against global compliance watchlists.';

  const handleCopyCaseId = async () => {
    if (!caseId || caseId === '—') return;

    try {
      await navigator.clipboard.writeText(caseId);
      dispatch(
        showSnackbar({
          message: labels.copySuccess ?? 'Case ID copied',
          severity: 'success',
        }),
      );
    } catch {
      dispatch(
        showSnackbar({
          message: labels.copyFailed ?? 'Unable to copy case ID',
          severity: 'error',
        }),
      );
    }
  };

  return (
    <Box sx={amlResultContainerSx}>
      {/* Header */}
      <Typography sx={getAmlResultTitleSx(theme)}>{title}</Typography>
      <Typography sx={getAmlResultSubtitleSx(theme)}>{subtitle}</Typography>

      {/* Alert banner */}
      {isMatched ? (
        <Box sx={getAmlResultAlertSx(theme)}>
          <ErrorOutlineIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {labels.matchAlert ?? 'Potential match detected. Manual compliance review recommended.'}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            mb: 2.5,
            borderRadius: 1,
            border: `1px solid ${theme.palette.success.light}`,
            bgcolor: theme.palette.success.light + '33',
            color: theme.palette.success.dark,
          }}
        >
          <CheckCircleOutlineIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {labels.noMatchAlert ?? 'No match found on global compliance watchlists.'}
          </Typography>
        </Box>
      )}

      {/* Metrics row */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Box sx={amlMetricBoxSx}>
          <Typography sx={getAmlMetricLabelSx(theme)}>{labels.matchScore ?? 'Match Score'}</Typography>
          <Typography
            sx={{
              ...getAmlMatchScoreValueSx(theme),
              color: isMatched ? theme.palette.error.main : theme.palette.success.main,
            }}
          >
            {formatMatchScore(data?.matchScore)}
          </Typography>
        </Box>

        <Box sx={amlMetricBoxSx}>
          <Typography sx={getAmlMetricLabelSx(theme)}>{labels.caseId ?? 'Case ID'}</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.text.primary, lineHeight: 1.2 }}>
              {caseId}
            </Typography>
            {caseId !== '—' && (
              <IconButton size="small" onClick={handleCopyCaseId} aria-label={labels.copyCaseId ?? 'Copy case ID'}>
                <ContentCopyOutlinedIcon fontSize="small" color="action" />
              </IconButton>
            )}
          </Stack>
        </Box>
      </Stack>

      {/* Flagged profile (match only) */}
      {isMatched && match && (
        <AmlFlaggedProfileCard match={match} data={data} labels={labels}>
          {extraResultCount > 0 && (
            <Link
              component="button"
              type="button"
              onClick={() => setResultsModalOpen(true)}
              sx={{ mt: 2, fontSize: '0.875rem', fontWeight: 600 }}
            >
              {getMoreResultsLabel(extraResultCount, labels)}
            </Link>
          )}
        </AmlFlaggedProfileCard>
      )}

      <AmlScreeningApiResultsModal
        open={resultsModalOpen}
        onClose={() => setResultsModalOpen(false)}
        results={screeningApiResults}
        labels={labels}
      />

      {/* Footer */}
      <Box sx={amlResultFooterSx}>
        <Button
          variant="text"
          startIcon={<ReplayOutlinedIcon fontSize="x-small" />}
          onClick={onReScreen}
          sx={amlReRunButtonSx}
        >
          {labels.reRunScreening ?? 'Re-run Screening'}
        </Button>
      </Box>
    </Box>
  );
}
